import React from "react";
import ReactDOM from "react-dom/client";
import { createHashRouter, Navigate, RouterProvider } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import { CountryProvider } from "./CountryContext";
import { theme as mantineTheme } from "./theme";

import "./index.css";

import LayoutWrapper from "./layoutWrapper";
import i18next from "./i18n/config";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/code-highlight/styles.css";
import "@mantine/notifications/styles.css";

import { MantineProvider } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { Browse } from "./pages/browse";
import { RouteErrorPage } from "./pages/RouteErrorPage";
import { ErrorBoundary } from "./components/ErrorBoundary";
import Layout from "./pages/layout/Layout";
import AdminLayout from "./pages/admin/AdminLayout";
import { ProtectedRoute } from "./ProtectedRoute";
import FeedbackAdministration from "./pages/admin/Feedback/FeedbackAdministration";
import FeedbackDetails from "./pages/admin/Feedback/FeedbackDetails";
import FeedbackInboxPage from "./pages/admin/Feedback/FeedbackInboxPage";
import FeedbackInsightsPage from "./pages/admin/Feedback/FeedbackInsightsPage";
import HistoryAdministration from "./pages/admin/History/HistoryAdministration";
import HistoryDetails from "./pages/admin/History/HistoryDetails";
import HistoryConversationsPage from "./pages/admin/History/HistoryConversationsPage";
import HistoryInsightsPage from "./pages/admin/History/HistoryInsightsPage";
import { UserAdministration } from "./pages/admin/Users/UserAdministration";
import { UserDetailsPage } from "./pages/admin/Users/UserDetailsPage";
import UserDirectoryPage from "./pages/admin/Users/UserDirectoryPage";
import UserAnalyticsPage from "./pages/admin/Users/UserAnalyticsPage";
import AzureSearchPage from "./pages/admin/AzureSearch/AzureSearchPage";
import IdeaAdministration from "./pages/admin/Ideas/IdeaAdministration";
import IdeaInboxPage from "./pages/admin/Ideas/IdeaInboxPage";
import IdeaDetails from "./pages/admin/Ideas/IdeaDetails";

const router = createHashRouter([
    {
        path: "/",
        element: <LayoutWrapper />, // provides auth + contexts
        errorElement: <RouteErrorPage />,
        children: [
            {
                element: <Layout />, // normal user layout
                children: [
                    { index: true, element: <Browse /> },
                    {
                        path: "*",
                        lazy: () => import("./pages/NoPage")
                    }
                ]
            },
            {
                element: <ProtectedRoute redirectTo="/" />,
                children: [
                    {
                        path: "administration",
                        element: <AdminLayout />,
                        children: [
                            { index: true, element: <Navigate to="feedback" replace /> },
                            {
                                path: "feedback",
                                element: <FeedbackAdministration />,
                                children: [
                                    { index: true, element: <Navigate to="inbox" replace /> },
                                    { path: "inbox", element: <FeedbackInboxPage /> },
                                    { path: "insights", element: <FeedbackInsightsPage /> }
                                ]
                            },
                            { path: "feedback/:feedbackId", element: <FeedbackDetails /> },
                            {
                                path: "history",
                                element: <HistoryAdministration />,
                                children: [
                                    { index: true, element: <Navigate to="conversations" replace /> },
                                    { path: "conversations", element: <HistoryConversationsPage /> },
                                    { path: "insights", element: <HistoryInsightsPage /> }
                                ]
                            },
                            { path: "history/:historyId", element: <HistoryDetails /> },
                            {
                                path: "users",
                                element: <UserAdministration />,
                                children: [
                                    { index: true, element: <Navigate to="directory" replace /> },
                                    { path: "directory", element: <UserDirectoryPage /> },
                                    { path: "analytics", element: <UserAnalyticsPage /> }
                                ]
                            },
                            { path: "users/:userId", element: <UserDetailsPage /> },
                            { path: "azure-search", element: <AzureSearchPage /> },
                            {
                                path: "ideas",
                                element: <IdeaAdministration />,
                                children: [
                                    { index: true, element: <Navigate to="inbox" replace /> },
                                    { path: "inbox", element: <IdeaInboxPage /> }
                                ]
                            },
                            { path: "ideas/:ideaId", element: <IdeaDetails /> }
                        ]
                    }
                ]
            }
        ]
    }
]);

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
    <React.StrictMode>
        <I18nextProvider i18n={i18next}>
            <MantineProvider theme={mantineTheme} classNamesPrefix="arthrex">
                <Notifications />
                <ErrorBoundary>
                    <RouterProvider router={router} />
                </ErrorBoundary>
            </MantineProvider>
        </I18nextProvider>
    </React.StrictMode>
);
